import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/lib/onboardingContext";
import { AuthProvider } from "@/lib/authContext";
import { SubscriptionProvider } from "@/lib/subscriptionContext";
import { I18nProvider } from "@/lib/i18nContext";
import { LocationProvider } from "@/lib/locationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeatureGate from "@/components/FeatureGate";
import Landing from "./pages/Landing";
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Locations = lazy(() => import("./pages/Locations"));
const MyLocations = lazy(() => import("./pages/MyLocations"));
const Timeline = lazy(() => import("./pages/Timeline"));
const Permits = lazy(() => import("./pages/Permits"));
const BudgetPage = lazy(() => import("./pages/Budget"));
const Checklist = lazy(() => import("./pages/Checklist"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Billing = lazy(() => import("./pages/Billing"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const Forecast = lazy(() => import("./pages/Forecast"));
const PlanPreview = lazy(() => import("./pages/PlanPreview"));
const Affiliates = lazy(() => import("./pages/Affiliates"));
const SharedRoadmap = lazy(() => import("./pages/SharedRoadmap"));
const CostCalculator = lazy(() => import("./pages/CostCalculator"));
const AdminListings = lazy(() => import("./pages/AdminListings"));
const Equipment = lazy(() => import("./pages/Equipment"));
const Inventory = lazy(() => import("./pages/Inventory"));
const StaffRota = lazy(() => import("./pages/StaffRota"));
const Operations = lazy(() => import("./pages/Operations"));
const OperationsDashboard = lazy(() => import("./pages/OperationsDashboard"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SupportedCities = lazy(() => import("./pages/SupportedCities"));
const Admin = lazy(() => import("./pages/Admin"));
const Terms = lazy(() => import("./pages/Terms"));
const StripeFlowChecklist = lazy(() => import("./pages/StripeFlowChecklist"));
const SettingsIntegrations = lazy(() => import("./pages/SettingsIntegrations"));
const Settings = lazy(() => import("./pages/Settings"));
const SquareCallback = lazy(() => import("./pages/SquareCallback"));
const OurStory = lazy(() => import("./pages/OurStory"));
const Mission = lazy(() => import("./pages/Mission"));
const Benchmarks = lazy(() => import("./pages/Benchmarks"));
const SharedBriefing = lazy(() => import("./pages/SharedBriefing"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <I18nProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <LocationProvider>
              <AppProvider>
                <BrowserRouter>
                  <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/enterprise" element={<Enterprise />} />
                    <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/locations" element={<ProtectedRoute><FeatureGate feature="locations.unlimited" featureName="Location browser"><Locations /></FeatureGate></ProtectedRoute>} />
                    <Route path="/my-locations" element={<ProtectedRoute><FeatureGate feature="multiLocation" featureName="My Locations"><MyLocations /></FeatureGate></ProtectedRoute>} />
                    <Route path="/timeline" element={<ProtectedRoute><FeatureGate feature="openingPlan" featureName="Opening Timeline"><Timeline /></FeatureGate></ProtectedRoute>} />
                    <Route path="/permits" element={<ProtectedRoute><Permits /></ProtectedRoute>} />
                    <Route path="/budget" element={<ProtectedRoute><FeatureGate feature="budget" featureName="Budget Planner"><BudgetPage /></FeatureGate></ProtectedRoute>} />
                    <Route path="/checklist" element={<ProtectedRoute><Checklist /></ProtectedRoute>} />
                    <Route path="/assistant" element={<ProtectedRoute><FeatureGate feature="aiAdvisor" featureName="AI Business Advisor"><Assistant /></FeatureGate></ProtectedRoute>} />
                    <Route path="/forecast" element={<ProtectedRoute><FeatureGate feature="forecast" featureName="Revenue Forecast"><Forecast /></FeatureGate></ProtectedRoute>} />
                    <Route path="/plan" element={<ProtectedRoute><PlanPreview /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                    <Route path="/billing" element={<Navigate to="/settings?section=billing" replace />} />
                    <Route path="/affiliates" element={<Affiliates />} />
                    <Route path="/shared" element={<SharedRoadmap />} />
                    <Route path="/calculator" element={<ProtectedRoute><CostCalculator /></ProtectedRoute>} />
                    <Route path="/admin/listings" element={<ProtectedRoute><AdminListings /></ProtectedRoute>} />
                    <Route path="/equipment" element={<ProtectedRoute><FeatureGate feature="equipment" featureName="Equipment Planner"><Equipment /></FeatureGate></ProtectedRoute>} />
                    <Route path="/inventory" element={<ProtectedRoute><FeatureGate feature="inventory" featureName="Inventory"><Inventory /></FeatureGate></ProtectedRoute>} />
                    <Route path="/staff-rota" element={<ProtectedRoute><FeatureGate feature="staffRota" featureName="Staff Rota"><StaffRota /></FeatureGate></ProtectedRoute>} />
                    <Route path="/operations" element={<ProtectedRoute><FeatureGate feature="operations" featureName="Operations"><Operations /></FeatureGate></ProtectedRoute>} />
                    <Route path="/dashboard/operations" element={<ProtectedRoute><OperationsDashboard /></ProtectedRoute>} />
                    <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/supported-cities" element={<SupportedCities />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/our-story" element={<OurStory />} />
                    <Route path="/mission" element={<Mission />} />
                    <Route path="/benchmarks" element={<Benchmarks />} />
                    <Route path="/briefing/:shareId" element={<SharedBriefing />} />
                    <Route path="/stripe-flow-checklist" element={<ProtectedRoute><StripeFlowChecklist /></ProtectedRoute>} />
                    <Route path="/settings/integrations" element={<Navigate to="/settings?section=integrations" replace />} />
                    <Route path="/auth/square/callback" element={<SquareCallback />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </BrowserRouter>
              </AppProvider>
            </LocationProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </I18nProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
